import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Send, MessageCircle, MapPin, Mail } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Query received — we will call you back shortly.");
      setForm({ name: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send right now. Please WhatsApp us directly.");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-brass/60 focus:border-brass transition";

  return (
    <section id="contact" className="py-16 sm:py-24" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-brass-dark font-semibold mb-4">Contact</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-ink">
            Tell us what you need filed, fixed or audited.
          </h2>
          <p className="mt-4 text-base text-ink-muted leading-relaxed max-w-md">
            Three fields, one callback. Or reach us instantly on WhatsApp — we reply during business hours across Ghaziabad and NCR.
          </p>
          <div className="mt-8 space-y-3">
            {["+91 96548 52550", "+91 96548 52529"].map((n, i) => (
              <a key={n} href={`tel:+9196548525${i === 0 ? "50" : "29"}`} data-testid={`contact-phone-${i + 1}`}
                className="flex items-center gap-3 text-ink font-semibold hover:text-brass-dark transition-colors duration-300">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brass-light text-brass-dark"><Phone size={16} /></span>
                {n}
              </a>
            ))}
            <a href="mailto:skch.ca@gmail.com" data-testid="contact-email-link"
              className="flex items-center gap-3 text-ink font-semibold hover:text-brass-dark transition-colors duration-300">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brass-light text-brass-dark"><Mail size={16} /></span>
              skch.ca@gmail.com
            </a>
            <div className="flex items-start gap-3 text-ink" data-testid="contact-address">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brass-light text-brass-dark"><MapPin size={16} /></span>
              <span className="text-sm leading-relaxed pt-2 font-medium">
                510, 5th Floor, Sumanglam Tower,<br />RDC Raj Nagar, Ghaziabad,<br />Uttar Pradesh 201002
              </span>
            </div>
            <a href="https://wa.me/919654852550?text=Hello%2C%20I%20need%20a%20Chartered%20Accountant%20in%20Ghaziabad."
              target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
              className="inline-flex items-center gap-2 mt-2 rounded-full bg-forest text-white text-sm font-semibold px-6 py-3 hover:bg-ink transition-colors duration-300">
              <MessageCircle size={16} /> WhatsApp Us Directly
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form"
            className="rounded-3xl bg-white border border-slate-200 p-8 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.2)] space-y-5">
            <div>
              <label htmlFor="contact-name" className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Full Name</label>
              <input id="contact-name" data-testid="contact-name-input" required minLength={2}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name" className={field} />
            </div>
            <div>
              <label htmlFor="contact-phone" className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Phone / WhatsApp</label>
              <input id="contact-phone" data-testid="contact-phone-input" required minLength={8} type="tel"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 ..." className={field} />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Required Service / Query</label>
              <textarea id="contact-message" data-testid="contact-message-input" required minLength={2} rows={4}
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="e.g. GST return filing for my Indirapuram shop" className={`${field} resize-none`} />
            </div>
            <button type="submit" disabled={sending} data-testid="contact-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-paper font-semibold px-6 py-3.5 hover:bg-forest disabled:opacity-60 transition-colors duration-300">
              {sending ? "Sending..." : <>Submit Query &amp; Get Callback <Send size={16} /></>}
            </button>
          </form>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="mt-14 rounded-3xl overflow-hidden border border-slate-200 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.2)]" data-testid="google-map-embed">
          <iframe
            title="Shashank Chaudhary & Co — 510, 5th Floor, Sumanglam Tower, RDC Raj Nagar, Ghaziabad"
            src="https://maps.google.com/maps?q=Sumanglam%20Tower%2C%20RDC%20Raj%20Nagar%2C%20Ghaziabad%2C%20Uttar%20Pradesh%20201002&t=&z=16&ie=UTF8&iwloc=&output=embed"
            className="w-full h-[320px] sm:h-[420px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          <div className="flex flex-wrap items-center justify-between gap-4 bg-white px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-ink-muted">
              <span className="font-semibold text-ink">510, 5th Floor, Sumanglam Tower</span> · RDC Raj Nagar, Ghaziabad, UP 201002
            </p>
            <a href="https://www.google.com/maps/dir/?api=1&destination=Sumanglam+Tower+RDC+Raj+Nagar+Ghaziabad+Uttar+Pradesh+201002"
              target="_blank" rel="noopener noreferrer" data-testid="get-directions-btn"
              className="inline-flex items-center gap-2 rounded-full bg-ink text-paper text-sm font-semibold px-5 py-2.5 hover:bg-forest transition-colors duration-300">
              <MapPin size={15} /> Get Directions
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
